// @view/document/review/SectionActionsContent.view.tsx
import React from "react";
import { toast } from "sonner";
import { Button } from "@components/ui/button";
import * as styles from "@styles/SectionActions.css";
import { useLoadingController } from "@src/contexts/Loading.context";

import { ok } from "@ps-bridge/ok";
import * as IO from "@ps-bridge/interop/io";
import type { OptionTs } from "@ps-bridge/interop";
import * as Option from "@utils/Option";
import type { DocumentReviewViewProps } from "@ps-bridge/contracts/documentReview";
import { readOptionString } from "@utils/readOptionString";

type SectionActionsContentViewProps = {
  sectionContents: OptionTs<string>;
} & Pick<DocumentReviewViewProps, "handleRefine" | "handleEdits" | "handleUpload">;

const SectionActionsContentView: React.FC<SectionActionsContentViewProps> = ({
  sectionContents,
  handleRefine,
  handleEdits,
  handleUpload,
}) => {
  const { setSuccess } = useLoadingController();

  const handleCopy = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      IO.run(setSuccess);
      toast.success("Content copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleCopyOption = () => {
    const content = readOptionString(sectionContents);
    return content.trim().length > 0 ? handleCopy(content) : Promise.resolve();
  };

  return (
    <div className={styles.actionCard}>
      <div className={styles.buttonContainer}>
        <Button
          className={styles.button}
          variant="outline"
          onClick={() => IO.run(handleRefine(ok))}
          asChild={Option.none()}
        >
          Refine
        </Button>
        <Button
          className={styles.button}
          variant="outline"
          onClick={() => IO.run(handleEdits(ok))}
          asChild={Option.none()}
        >
          Edit
        </Button>
        <Button
          className={styles.button}
          variant="outline"
          onClick={() => IO.run(handleUpload(ok))}
          asChild={Option.none()}
        >
          Upload
        </Button>
        <Button
          className={styles.button}
          variant="outline"
          onClick={handleCopyOption}
          asChild={Option.none()}
        >
          Copy Markdown
        </Button>
      </div>
    </div>
  );
};

export default SectionActionsContentView;
